import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan, useCanWithoutBuilding } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { RowsSkeleton } from "@/routes/_authenticated/_administration/settings/-components/rows-skeleton";
import { MessageTemplateForm } from "./-components/messaging/message-template-form";
import {
  MessageTemplateList,
  type MessageTemplateRow,
} from "./-components/messaging/message-template-list";
import { MessageTemplatePreview } from "./-components/messaging/message-template-preview";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";

export const Route = createFileRoute(
  "/_authenticated/_administration/settings/ats-message-templates",
)({
  beforeLoad: ({ context }) => guardRoute(context.me, "ats-message-templates"),
  component: AtsMessageTemplatesSettings,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** A template belongs to one building, so writing one needs a building chosen; reading works across
 * all. */
function AtsMessageTemplatesSettings() {
  const { facilityId } = useFacility();
  const realFacility = facilityId === "all" ? null : facilityId;
  const canManage = useCanWithoutBuilding("hiring:manage");
  const canSeeFacilities = useCan("facility:read");
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MessageTemplateRow | null>(null);
  const [previewing, setPreviewing] = useState<MessageTemplateRow | null>(null);
  const [deleting, setDeleting] = useState<MessageTemplateRow | null>(null);

  const templates = useQuery(
    orpc.workforce.hiringCatalog.listMessageTemplates.queryOptions({ input: { facilityId } }),
  );
  const facilities = useQuery({
    ...orpc.administration.facilities.list.queryOptions({ input: { includeInactive: true } }),
    enabled: canSeeFacilities,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.workforce.hiringCatalog.key() });

  const save = useMutation(
    orpc.workforce.hiringCatalog.saveMessageTemplate.mutationOptions({
      onSuccess: async () => {
        toast.success("Template saved");
        setCreating(false);
        setEditing(null);
        await invalidate();
      },
      onError: fail,
    }),
  );
  const remove = useMutation(
    orpc.workforce.hiringCatalog.deleteMessageTemplate.mutationOptions({
      onSuccess: async () => {
        toast.success("Template deleted");
        setDeleting(null);
        await invalidate();
      },
      onError: fail,
    }),
  );

  const rows = templates.data ?? [];
  const facilityNames = new Map(
    (facilities.data ?? []).map((facility) => [facility.id, facility.name]),
  );

  /* Archiving is a status change on the whole row, so every column travels with
     it: `saveMessageTemplate` replaces the record rather than patching it. */
  const toggleArchive = (template: MessageTemplateRow) =>
    save.mutate({
      templateId: template.id,
      facilityId: template.facilityId,
      name: template.name,
      body: template.body,
      status: template.status === "active" ? "inactive" : "active",
    });

  return (
    <>
      <PageHeader
        title="Candidate message templates"
        meta="Text messages a recruiter picks when writing to a job candidate, with the candidate's details filled in."
      />

      {canManage && !realFacility ? (
        <OneBuildingCard
          prompt={"Every building's templates are listed below. To add one, choose its building."}
        />
      ) : null}

      <RecordCard
        title="Templates"
        count={templates.data ? rows.length : undefined}
        canWrite={canManage && Boolean(realFacility)}
        addLabel="Add template"
        onAdd={() => setCreating(true)}
      >
        {templates.isPending ? (
          <RowsSkeleton />
        ) : templates.isError ? (
          <ReadFailed
            message="The templates could not be loaded."
            error={templates.error}
            retrying={templates.isFetching}
            onRetry={() => void templates.refetch()}
          />
        ) : (
          <MessageTemplateList
            templates={rows}
            facilityNames={facilityNames}
            canManage={canManage}
            busy={save.isPending}
            onPreview={setPreviewing}
            onEdit={setEditing}
            onToggleArchive={toggleArchive}
            onDelete={setDeleting}
          />
        )}
      </RecordCard>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add template</DialogTitle>
            <DialogDescription>
              Merge fields are resolved for each candidate when the message is sent.
            </DialogDescription>
          </DialogHeader>
          {realFacility ? (
            <MessageTemplateForm
              facilityId={realFacility}
              pending={save.isPending}
              submitLabel="Add template"
              onCancel={() => setCreating(false)}
              onSubmit={(values) => save.mutate(values)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : setEditing(null))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit template</DialogTitle>
            <DialogDescription>Changes apply to messages sent from now on.</DialogDescription>
          </DialogHeader>
          {editing ? (
            <MessageTemplateForm
              facilityId={editing.facilityId}
              pending={save.isPending}
              submitLabel="Save template"
              defaultValues={{
                templateId: editing.id,
                facilityId: editing.facilityId,
                name: editing.name,
                body: editing.body,
                status: editing.status,
              }}
              onCancel={() => setEditing(null)}
              onSubmit={(values) => save.mutate({ ...values, templateId: editing.id })}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <MessageTemplatePreview template={previewing} onClose={() => setPreviewing(null)} />

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? null : setDeleting(null))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleting?.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              It is gone for good. Archive it instead to keep it for reference. Messages already
              sent are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => {
                if (deleting) remove.mutate({ templateId: deleting.id });
              }}
            >
              {remove.isPending ? "Deleting…" : "Delete template"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
